import styled from 'styled-components'

export const InfoSection = styled.div`
  margin-bottom: 12px;
  overflow: visible;
  position: relative;
  transition: all 0.2s;

  &:hover {
    box-shadow: none;
  }
`

export const PropertySectionHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const PropertySectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
`

export const SectionIcon = styled.span`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`

export const SectionCount = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.background.tertiary};
  padding: 2px 6px;
  border-radius: 4px;
`

export const SectionContent = styled.div`
  padding: 12px;
  position: relative;
  overflow: visible;
`

export const PropertyDataGrid = styled.div<{ $gap?: string | number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => (typeof $gap === 'number' ? `${$gap}px` : $gap || '0')};
  position: relative;
  overflow: visible;
`

export const PropertyDataRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  gap: 12px;
  position: relative;
  overflow: visible;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

export const DataLabel = styled.span<{ $hasTooltip?: boolean }>`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 1;
  min-width: 0;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: ${({ $hasTooltip }) => ($hasTooltip ? 'help' : 'default')};

  ${({ $hasTooltip }) =>
    $hasTooltip &&
    `
    &::after {
      content: '';
      width: 13px;
      height: 13px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpath d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'/%3E%3Cdot cx='12' cy='17' r='.5'/%3E%3C/svg%3E");
      background-size: contain;
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    
    &:hover::after {
      opacity: 0.5;
    }
  `}
`

export const DataValue = styled.span<{ $large?: boolean; $color?: string }>`
  font-size: ${({ $large }) => ($large ? '14px' : '12px')};
  font-weight: ${({ $large }) => ($large ? '600' : '500')};
  color: ${({ $color, theme }) => $color || theme.colors.text.primary};
  text-align: right;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;

  &.large {
    font-size: 14px;
    font-weight: 600;
  }

  svg {
    vertical-align: middle;
  }
`

export const DataValueWithUnit = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
  text-align: right;
`

export const DataUnit = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-weight: 400;
`

export const ExpandableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const ExpandableItem = styled.div<{ $expanded?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 6px;
  overflow: visible;
  position: relative;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.primary};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &.expanded .expand-icon {
    transform: rotate(180deg);
  }

  &.expanded .expandable-content {
    max-height: 400px;
  }
`

export const ExpandableHeader = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const ExpandableInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`

export const ExpandableInfoWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

export const ExpandableTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ExpandableSubtitle = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

export const ExpandableContent = styled.div<{ $expanded?: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '2000px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  overflow: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

export const ExpandableDetails = styled.div`
  padding: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  gap: 0;

  ${PropertyDataRow} {
    padding-bottom: 8px;
    overflow: visible;
  }
`

export const PropertyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 5px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;

  svg {
    width: 10px;
    height: 10px;
  }
`

export const PropertyMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const PropertyStatusBadge = styled.div<{
  $status: 'active' | 'inactive' | 'protected'
}>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;

  ${({ $status, theme }) => {
    switch ($status) {
      case 'active':
        return `
          background: #E8F5E8;
          color: ${theme.colors.success};
        `
      case 'inactive':
        return `
          background: #FFF5E6;
          color: ${theme.colors.warning};
        `
      case 'protected':
        return `
          background: #FFE6E6;
          color: ${theme.colors.danger};
        `
      default:
        return `
          background: ${theme.colors.background.tertiary};
          color: ${theme.colors.text.secondary};
        `
    }
  }}
`

export const ColoredBadge = styled.div<{
  $type:
    | 'mountain_view'
    | 'public_transport'
    | 'travel_time'
    | 'lake_view'
    | 'noise'
    | 'distance_to_highvoltage_powerlines'
    | 'slope'
    | 'default'
  $value?: string | number
}>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;

  ${({ $type, $value }) => {
    const value = $value?.toString().toLowerCase() || ''

    // Mountain view colors
    if ($type === 'mountain_view') {
      switch (value) {
        case '0 up to 4 peaks':
          return `
            background: #FFF5E6;
            color: #fc8d59;
          `
        case '5 up to 13 peaks':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        case 'more than 13 peaks':
          return `
            background: #E8F5E8;
            color: #91cf60;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // Lake view colors
    if ($type === 'lake_view') {
      switch (value) {
        case '0 ha':
          return `
            background: #FFF5E6;
            color: #fc8d59;
          `
        case 'more than 0 up to 100ha':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        case 'more than 100ha':
          return `
            background: #E8F5E8;
            color: #91cf60;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // Slope colors
    if ($type === 'slope') {
      switch (value) {
        case 'more than 9 degrees':
          return `
            background: #FFF5E6;
            color: #fc8d59;
          `
        case 'more than 4 up to 9 degrees':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        case '0 up to 4 degrees':
          return `
            background: #E8F5E8;
            color: #91cf60;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // Public transport quality colors
    if ($type === 'public_transport') {
      switch (value) {
        case 'very well served':
          return `
            background: #E8F5E8;
            color: #1a9641;
          `
        case 'well served':
          return `
            background: #F0F8F0;
            color: #5ba85b;
          `
        case 'medium served':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        case 'badly served':
          return `
            background: #FFF5E6;
            color: #fdae61;
          `
        case 'very badly served':
          return `
            background: #FFE6E6;
            color: #d7191c;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // Travel time colors
    if ($type === 'travel_time') {
      switch (value) {
        case 'short':
          return `
            background: #E8F5E8;
            color: #91cf60;
          `
        case 'medium':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        case 'long':
          return `
            background: #FFF5E6;
            color: #fc8d59;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // distance to high-voltage powerlines
    if ($type === 'distance_to_highvoltage_powerlines') {
      switch (value) {
        case 'over 100m':
          return `
            background: #E8F5E8;
            color: #91cf60;
          `
        case '0 up to 100m':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // Noise levels
    if ($type === 'noise') {
      switch (value) {
        case 'up to 45 db':
          return `
            background: #E8F5E8;
            color: #91cf60;
          `
        case '45 to 52 db':
          return `
            background: #FFFEF0;
            color: #b8860b;
          `
        case 'more than 52 db':
          return `
            background: #FFF5E6;
            color: #fc8d59;
          `
        default:
          return `
            background: #f5f5f5;
            color: #666;
          `
      }
    }

    // Default fallback
    return `
      background: #f5f5f5;
      color: #666;
    `
  }}
`

// Building Icon with two variants for different use cases
export const BuildingIcon = styled.div<{ $variant?: 'small' | 'large' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $variant }) => ($variant === 'large' ? '6px' : '4px')};
  background: ${({ $variant }) =>
    $variant === 'large' ? 'rgba(255, 122, 0, 0.1)' : '#fff5e6'};
  color: ${({ $variant }) => ($variant === 'large' ? '#ff7a00' : '#ff9500')};
  flex-shrink: 0;

  ${({ $variant }) =>
    $variant === 'large'
      ? `
    width: 60px;
    height: 60px;
    margin-right: 10px;
    
    svg {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }
  `
      : `
    width: 20px;
    height: 20px;
    margin-right: 6px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  `}
`

export const ZoneType = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  background: rgba(0, 173, 230, 0.1);
  color: #0099cc;
`

export const RestrictionStatus = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;

  &.status-active {
    color: ${({ theme }) => theme.colors.danger};
  }

  &.status-inactive {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &::before {
    content: '●';
  }
`

// Owner specific styles
export const OwnerCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.primary};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &:last-child {
    margin-bottom: 0;
  }
`

export const OwnerName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`

export const OwnerActions = styled.div`
  display: flex;
  gap: 6px;
`

export const OwnerActionBtn = styled.button<{
  $variant?: 'google' | 'linkedin' | 'local' | 'company' | 'zefix'
}>`
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 10px;
  font-weight: 600;

  svg {
    width: 14px;
    height: 14px;
  }

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'google':
        return `
          background: #DB4437;
          color: white;
          &:hover { background: #C23321; }
        `
      case 'linkedin':
        return `
          background: #0077B5;
          color: white;
          &:hover { background: #006399; }
        `
      case 'local':
        return `
          background: white;
          border: 1px solid ${theme.colors.border.primary};
          color: ${theme.colors.text.primary};
          &:hover { background: ${theme.colors.background.tertiary}; }
        `
      case 'company':
        return `
          background: #E6F7FC;
          color: ${theme.colors.primary};
          &:hover { background: #CCF0FA; }
        `
      case 'zefix':
        return `
          background: #D52B1E;
          color: white;
          &:hover { background: #B52419; }
        `
      default:
        return `
          background: white;
          border: 1px solid ${theme.colors.border.primary};
          color: ${theme.colors.text.primary};
          &:hover { background: ${theme.colors.background.tertiary}; }
        `
    }
  }}
`

export const LastChange = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: right;
  margin-bottom: 12px;
`

export const HoverExplanation = styled.div`
  position: fixed;
  z-index: 2147483647;
  left: 0;
  top: 0;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.98);
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 400;
  white-space: normal;
  word-wrap: break-word;
  width: 280px;
  pointer-events: none;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 3px 8px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  opacity: 0;
  transform: scale(0.96);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.98);
    border-left: 1px solid rgba(0, 0, 0, 0.06);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    transform: rotate(45deg);
  }

  @media (max-width: 768px) {
    width: min(280px, calc(100vw - 40px));
    left: 50%;
    transform: translateX(-50%) translateY(-4px) scale(0.96);

    &.visible {
      transform: translateX(-50%) translateY(0) scale(1);
    }

    &::before {
      left: 50%;
      transform: translateX(-50%) rotate(45deg);
    }
  }
`

export const DataLabelWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`
